import { motion } from 'framer-motion';
import VideoCard from './VideoCard';

const FALLBACK_BACKGROUND = 'linear-gradient(135deg, #070604 0%, #15100a 48%, #050505 100%)';

function normalizeParagraphs(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string');
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return [value];
  }

  return [];
}

const BookLanding = ({ bookData, chapter }) => {
  const isChapterPage = Boolean(chapter);
  const pageData = chapter || bookData || {};
  const paragraphs = normalizeParagraphs(isChapterPage ? pageData.narrative : pageData.authorText);
  const backgroundVideoUrl = pageData.backgroundVideoUrl || bookData?.backgroundVideoUrl || '';
  const hasBackgroundVideo = typeof backgroundVideoUrl === 'string' && backgroundVideoUrl.trim() !== '';

  return (
    <main className="book-landing min-h-screen overflow-hidden bg-void text-white" data-testid="book-landing">
      <div className="fixed inset-0 -z-10" aria-hidden="true">
        {hasBackgroundVideo ? (
          <video
            className="h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          >
            <source src={backgroundVideoUrl} type="video/mp4" />
          </video>
        ) : (
          <div className="h-full w-full" style={{ background: FALLBACK_BACKGROUND }} />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 book-landing-vignette" />
      </div>

      <section className="relative mx-auto flex min-h-screen w-full max-w-5xl flex-col items-center justify-center px-5 py-16 text-center sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="w-full"
        >
          <p className="mb-4 font-cinzel text-xs uppercase tracking-[0.34em] text-[#D8BB6A]/85 sm:text-sm">
            Book Companion
          </p>

          <h1 className="book-landing-title mx-auto max-w-4xl text-[#E9C968]">
            <span className="book-title-line">The <span className="book-title-emphasis">OATH</span></span>
            <span className="book-title-line book-title-middle">of the</span>
            <span className="book-title-line book-title-unhuan">UNHUAN</span>
          </h1>

          <div className="mx-auto mt-7 h-px w-28 bg-gradient-to-r from-transparent via-[#E9C968] to-transparent" />

          <div className="mx-auto mt-8 max-w-3xl">
            <p className={isChapterPage
              ? 'book-chapter-subtitle font-cinzel text-3xl uppercase text-[#F3DE9B] sm:text-4xl'
              : 'font-cormorant text-2xl italic text-[#F3DE9B] sm:text-3xl'}
            >
              {isChapterPage ? pageData.subtitle : pageData.byline || 'A Note From The Author'}
            </p>

            {isChapterPage ? (
              <div className="book-yahi-note mx-auto mt-8 max-w-3xl text-left">
                <div className="space-y-4 font-cormorant text-xl italic leading-8 text-[#F7EED0] sm:text-2xl sm:leading-9">
                  {paragraphs.map((paragraph, index) => (
                    paragraph.trim().length > 0 ? (
                      <p key={`${paragraph}-${index}`}>{paragraph}</p>
                    ) : (
                      <div key={`spacer-${index}`} className="h-3" aria-hidden="true" />
                    )
                  ))}
                </div>
                <div className="book-yahi-signature mt-5 text-right">
                  <p className="text-[#F3DE9B]">
                    {pageData.signatureIntro || '—'} {pageData.signatureName || 'Goddess Yahi'}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-5 space-y-4 font-cormorant text-xl leading-8 text-[#F7EED0] sm:text-2xl sm:leading-9">
                  {paragraphs.map((paragraph, index) => (
                    paragraph.trim().length > 0 ? (
                      <p key={`${paragraph}-${index}`}>{paragraph}</p>
                    ) : (
                      <div key={`spacer-${index}`} className="h-3" aria-hidden="true" />
                    )
                  ))}
                </div>

                <div className="book-author-signature mx-auto mt-8 max-w-md">
                  <p className="font-cormorant text-xl italic text-[#F7EED0]/90">
                    {pageData.signatureIntro || 'With gratitude,'}
                  </p>
                  <p className="mt-1 font-cormorant text-4xl italic text-[#F3DE9B] sm:text-5xl">
                    {pageData.signatureName || 'Mahesh Thakur'}
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>

        <motion.div
          id="book-trailer"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className={isChapterPage ? 'mt-10 w-full max-w-3xl' : 'book-trailer-section mt-16 w-full max-w-3xl pt-10'}
        >
          <p className={isChapterPage
            ? 'mb-4 font-cinzel text-xs uppercase tracking-[0.28em] text-[#D8BB6A]/80'
            : 'book-trailer-heading mb-6 font-cinzel text-3xl uppercase text-[#F3DE9B] sm:text-4xl'}
          >
            {pageData.videoHeading || 'Featured Video'}
          </p>
          <VideoCard videoUrl={pageData.youtubeUrl || ''} className="book-landing-video" />
        </motion.div>
      </section>
    </main>
  );
};

export default BookLanding;
